
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { 
  getFeedPosts, 
  getUserPosts, 
  getPetPosts,
  getPostById, 
  likePost, 
  unlikePost, 
  addComment, 
  getPostComments,
  getCommentReplies,
  deletePost,
  deleteComment,
  createPost,
  getAllUserPetPosts,
  updatePost,
  deletePostMedia
} from "../services/postsService";
import { useAuth } from "../contexts/AuthContext";

export function useInfiniteFeedPosts() {
  const { user } = useAuth();
  
  // Detect connection speed (simple heuristic with type safety)
  const connection = (navigator as any).connection;
  const isSlowConnection = connection?.effectiveType === 'slow-2g' || 
                          connection?.effectiveType === '2g' ||
                          (connection?.downlink && connection.downlink < 1);
  
  // Adaptive page sizes based on connection
  const initialPageSize = isSlowConnection ? 15 : 25;
  const subsequentPageSize = isSlowConnection ? 10 : 20;
  
  return useInfiniteQuery({
    queryKey: ['posts', 'feed', 'infinite'],
    queryFn: ({ pageParam = 0 }) => {
      const pageSize = pageParam === 0 ? initialPageSize : subsequentPageSize;
      return getFeedPosts(pageParam, pageSize);
    },
    enabled: !!user,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const expectedSize = allPages.length === 1 ? initialPageSize : subsequentPageSize;
      // If the last page has fewer posts than expected, we've reached the end
      return lastPage.length === expectedSize ? allPages.length : undefined;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useFeedPosts() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['posts', 'feed'],
    queryFn: () => getFeedPosts(0, 20), // Keep existing behavior for backward compatibility
    enabled: !!user,
  });
}

// Hook for featured memories on landing page (no auth required)
// Strategy: Start with 24h refresh during low user activity phase
// As user base grows, reduce to: 12h → 6h → 3h → 1h → 30min → 10min (optimal)
export function useFeaturedMemories(limit = 6, excludeLostAlerts = false) {
  return useQuery({
    queryKey: ['posts', 'featured', limit, excludeLostAlerts],
    queryFn: () => getFeedPosts(0, limit, excludeLostAlerts),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - optimal for current user base
    gcTime: 1000 * 60 * 60 * 25, // 25 hours
  });
}

export function useUserPosts(userId: string | undefined) {
  return useQuery({
    queryKey: ['posts', 'user', userId],
    queryFn: async () => {
      if (!userId) {
        return [];
      }
      
      try {
        const result = await getUserPosts(userId);
        return result;
      } catch (error) {
        console.error('Error fetching user posts:', error);
        throw error;
      }
    },
    enabled: !!userId,
  });
}

// Hook to get all posts from all of a user's pets
export function useAllUserPetPosts(userId: string | undefined) {
  return useQuery({
    queryKey: ['posts', 'userAllPets', userId],
    queryFn: async () => {
      if (!userId) {
        return [];
      }
      
      try {
        const result = await getAllUserPetPosts(userId);
        return result;
      } catch (error) {
        console.error('Error fetching user pet posts:', error);
        throw error;
      }
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function usePetPosts(petId: string | undefined) {
  return useQuery({
    queryKey: ['posts', 'pet', petId],
    queryFn: () => getPetPosts(petId!),
    enabled: !!petId,
  });
}

export function usePost(postId: string | undefined) {
  return useQuery({
    queryKey: ['posts', postId],
    queryFn: () => getPostById(postId!),
    enabled: !!postId,
  });
}

export function usePostActions() {
  const queryClient = useQueryClient();
  
  const likeMutation = useMutation({
    mutationFn: likePost,
    onMutate: async (postId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['posts'] });
      await queryClient.cancelQueries({ queryKey: ['posts', postId] });
      
      // Snapshot previous values
      const previousPost = queryClient.getQueryData(['posts', postId]);
      const previousPosts = queryClient.getQueriesData({ queryKey: ['posts'] });
      
      // Optimistically update post data
      queryClient.setQueryData(['posts', postId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          likes_count: old.likes_count + 1,
          user_has_liked: true
        };
      });
      
      // Optimistically update posts lists
      queryClient.setQueriesData({ queryKey: ['posts'] }, (old: any) => {
        if (!old) return old;
        if (Array.isArray(old)) {
          return old.map(post => 
            post.id === postId 
              ? { ...post, likes_count: post.likes_count + 1, user_has_liked: true }
              : post
          );
        }
        return old;
      });
      
      return { previousPost, previousPosts };
    },
    onError: (err, postId, context) => {
      // Rollback on error
      if (context?.previousPost) {
        queryClient.setQueryData(['posts', postId], context.previousPost);
      }
      context?.previousPosts?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: (_, __, postId) => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', postId] });
    }
  });

  const unlikeMutation = useMutation({
    mutationFn: unlikePost,
    onMutate: async (postId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['posts'] });
      await queryClient.cancelQueries({ queryKey: ['posts', postId] });
      
      // Snapshot previous values
      const previousPost = queryClient.getQueryData(['posts', postId]);
      const previousPosts = queryClient.getQueriesData({ queryKey: ['posts'] });
      
      // Optimistically update post data
      queryClient.setQueryData(['posts', postId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          likes_count: Math.max(0, old.likes_count - 1),
          user_has_liked: false
        };
      });
      
      // Optimistically update posts lists
      queryClient.setQueriesData({ queryKey: ['posts'] }, (old: any) => {
        if (!old) return old;
        if (Array.isArray(old)) {
          return old.map(post => 
            post.id === postId 
              ? { ...post, likes_count: Math.max(0, post.likes_count - 1), user_has_liked: false }
              : post
          );
        }
        return old;
      });
      
      return { previousPost, previousPosts };
    },
    onError: (err, postId, context) => {
      // Rollback on error
      if (context?.previousPost) {
        queryClient.setQueryData(['posts', postId], context.previousPost);
      }
      context?.previousPosts?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: (_, __, postId) => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', postId] });
    }
  });

  const commentMutation = useMutation({
    mutationFn: ({ 
      postId,
      content,
      parentCommentId
    }: { 
      postId: string, 
      content: string,
      parentCommentId?: string
    }) => addComment(postId, content, parentCommentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['comments', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
  
  const deleteMutation = useMutation({
    mutationFn: deletePost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: deleteComment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      queryClient.invalidateQueries({ queryKey: ['replies'] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  const createPostMutation = useMutation({
    mutationFn: createPost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  const updatePostMutation = useMutation({
    mutationFn: ({ postId, updates }: { postId: string; updates: { caption?: string; privacy_setting?: 'public' | 'private' | 'followers_only' } }) => 
      updatePost(postId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', variables.postId] });
    },
  });

  const deleteMediaMutation = useMutation({
    mutationFn: (mediaId: string) => deletePostMedia(mediaId),
    onSuccess: (postId) => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      if (postId) queryClient.invalidateQueries({ queryKey: ['posts', postId] });
      queryClient.invalidateQueries({ queryKey: ['admin-user-posts'] });
    },
  });

  return {
    likePost: likeMutation.mutate,
    unlikePost: unlikeMutation.mutate,
    addComment: commentMutation.mutate,
    createPost: createPostMutation.mutate,
    deletePost: (postId: string, options?: { onSuccess?: () => void; onError?: (error?: Error) => void }) => {
      deleteMutation.mutate(postId, {
        onSuccess: () => {
          options?.onSuccess?.();
        },
        onError: (error: any) => {
          options?.onError?.(error);
        }
      });
    },
    updatePost: (postId: string, updates: { caption?: string; privacy_setting?: 'public' | 'private' | 'followers_only' }, options?: { onSuccess?: () => void; onError?: (error?: Error) => void }) => {
      updatePostMutation.mutate({ postId, updates }, {
        onSuccess: () => {
          options?.onSuccess?.();
        },
        onError: (error: any) => {
          options?.onError?.(error);
        }
      });
    },
    deleteComment: (commentId: string, options?: { onSuccess?: () => void; onError?: (error?: Error) => void }) => {
      deleteCommentMutation.mutate(commentId, {
        onSuccess: () => {
          options?.onSuccess?.();
        },
        onError: (error: any) => {
          options?.onError?.(error);
        }
      });
    },
    deletePostMedia: (mediaId: string, options?: { onSuccess?: () => void; onError?: (error?: Error) => void }) => {
      deleteMediaMutation.mutate(mediaId, {
        onSuccess: () => options?.onSuccess?.(),
        onError: (error: any) => options?.onError?.(error),
      });
    },
    isDeletingMedia: deleteMediaMutation.isPending,
    isLiking: likeMutation.isPending,
    isUnliking: unlikeMutation.isPending,
    isCommenting: commentMutation.isPending,
    isCreatingPost: createPostMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDeletingComment: deleteCommentMutation.isPending,
    isUpdating: updatePostMutation.isPending,
  };
}

export function usePostComments(postId: string | undefined) {
  return useQuery({
    queryKey: ['comments', postId],
    queryFn: () => getPostComments(postId!),
    enabled: !!postId,
  });
}

export function useCommentReplies(commentId: string | undefined) {
  return useQuery({
    queryKey: ['replies', commentId],
    queryFn: () => getCommentReplies(commentId!),
    enabled: !!commentId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}
